import React, { Component, ErrorInfo, ReactNode } from 'react';
import Icon from './Icon';
import { reportError } from '../utils/errorReporter';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
    reportError(error, { componentStack: errorInfo.componentStack });
  }

  componentDidUpdate(prevProps: Props) {
    // Reset error state when children change (e.g. navigation)
    if (this.state.hasError && prevProps.children !== this.props.children) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="fh-errboundary">
          <div className="fh-errboundary__icon">
            <Icon name="error" size={32} color="#ef5350" />
          </div>
          <h2 className="fh-errboundary__title">Something went wrong</h2>
          <p className="fh-errboundary__msg">An unexpected error occurred. Please try again.</p>
          <button onClick={this.handleRetry} className="fh-errboundary__btn-primary">
            Try again
          </button>
          <button
            onClick={() => { window.location.href = '/app/home'; }}
            className="fh-errboundary__btn-secondary"
          >
            Go Home
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
